import { useState, useEffect } from 'react';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '@municipality/firebase';
import { municipalitiesService, Municipality } from '../services/municipalities';

// Extended municipality interface with all fields
export interface ExtendedMunicipality extends Municipality {
  logoUrl?: string;
  coverImageUrl?: string;
  website?: string;
  contactEmail?: string;
  contactPhone?: string;
  description?: string;
  address?: string;
  timezone?: string;
  primaryLanguage?: string;
  availableLanguages?: string[];
  translations?: Record<string, {
    name?: string;
    address?: string;
  }>;
}

export const useMunicipalityDetails = (municipalityId: string | null) => {
  const [municipality, setMunicipality] = useState<ExtendedMunicipality | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!municipalityId) {
      setMunicipality(null);
      setLoading(false);
      return;
    }

    const fetchMunicipality = async () => {
      try {
        setLoading(true);
        setError(null);
        
        // Fetch basic municipality data
        const basicData = await municipalitiesService.getMunicipalityById(municipalityId);
        
        // Fetch extended fields directly from Firestore
        const docRef = doc(db, 'municipalities', municipalityId);
        const docSnap = await getDoc(docRef);
        
        if (docSnap.exists()) {
          const data = docSnap.data();
          const extendedData: ExtendedMunicipality = {
            ...basicData!,
            logoUrl: data.logoUrl || data.logo,
            coverImageUrl: data.coverImageUrl || data.coverImage,
            website: data.website,
            contactEmail: data.contactEmail,
            contactPhone: data.contactPhone || data.contactPhone2,
            description: data.description,
            address: data.address,
            timezone: data.timezone,
            primaryLanguage: data.primaryLanguage,
            availableLanguages: data.availableLanguages,
            translations: data.translations,
          };
          setMunicipality(extendedData);
        } else {
          setMunicipality(basicData as ExtendedMunicipality);
        }
      } catch (err: unknown) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to fetch municipality';
        setError(errorMessage);
      } finally {
        setLoading(false);
      }
    };

    fetchMunicipality();
  }, [municipalityId]);

  return { municipality, loading, error };
};
